// File: src/components/Header.tsx

import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "./CartContext";
import logo from '../../assets/AyushVatikaLogo.png';
import {
  FaBars,
  FaChevronDown,
  FaSearch,
  FaShoppingBag,
} from "react-icons/fa";
import "./header.css";
import { AuthModal } from "./AuthModal";
import { Sidebar, SidebarItem } from "./sidebar";


export interface HeaderProps {
  onToggleSidebar: (isOpen: boolean, height: number) => void;
  user: {
    id?: string;
    name: string;
    email: string;
    picture?: string;
    mobile?: string;
    gender?: string;
  } | null;
  setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>;
  onLogout: () => void;
}

interface GoogleUser {
  name: string;
  picture: string;
  email: string;
  sub?: string;
}

interface Category {
  id: number;
  name: string;
  icon: string | null;
  parentId: number | null;
}

// FIX: Added 'user' to destructuring to catch prop updates from parent
const Header: React.FC<HeaderProps> = ({ onToggleSidebar, user }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [sidebarCategories, setSidebarCategories] = useState<SidebarItem[]>([]);
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);

  const navigate = useNavigate();
  const { cart } = useCart();
  const itemCount = (cart || []).reduce((acc: any, item: { quantity: any; }) => acc + item.quantity, 0);

  const [googleUser, setGoogleUser] = useState<GoogleUser | null>(null);

  const userDropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const u = JSON.parse(storedUser);
          setGoogleUser(u);
        } catch (error) {
          console.error("Failed to parse user from localStorage:", error);
          localStorage.removeItem("user");
        }
      }
    };
    loadUser();

    // FIX: Listen for custom event 'userDataUpdated' from ProfilePage
    const handleUserUpdate = () => {
      loadUser();
    };
    window.addEventListener('userDataUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('userDataUpdated', handleUserUpdate);
    };
  }, []);
  
  // FIX: Sync state if 'user' prop updates from parent
  useEffect(() => {
    if (user) {
      setGoogleUser(user as GoogleUser);
    }
  }, [user]);

  // This is the key change: pass the header height to the parent component
  useEffect(() => {
    if (isSidebarOpen && headerRef.current) {
      const headerHeight = headerRef.current.offsetHeight;
      onToggleSidebar(isSidebarOpen, headerHeight);
    } else {
      onToggleSidebar(isSidebarOpen, 0); // Pass 0 when sidebar is closed
    }
  }, [isSidebarOpen, onToggleSidebar]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSidebarOpen]);

  const buildCategoryTree = (categories: Category[]): SidebarItem[] => {
    const categoryMap = new Map<number, SidebarItem>();
    const rootCategories: SidebarItem[] = [];

    categories.forEach(cat => {
      const sidebarItem: SidebarItem = {
        id: cat.id.toString(),
        name: cat.name,
        path: `/category/${cat.id}`,
        icon: cat.icon || undefined,
        children: [],
        parentId: cat.parentId !== null ? cat.parentId.toString() : undefined
      };
      categoryMap.set(cat.id, sidebarItem);
    });

    categoryMap.forEach(item => {
      if (item.parentId) {
        const parent = categoryMap.get(parseInt(item.parentId));
        if (parent) {
          parent.children?.push(item);
        }
      } else {
        rootCategories.push(item);
      }
    });

    rootCategories.forEach(root => {
      if (root.children) {
        root.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return rootCategories.sort((a, b) => a.name.localeCompare(b.name));
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('https://www.ayushvatika.com/api/categories');
      // const response = await fetch('https://www.ayushvatika.com/api/categories');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: Category[] = await response.json();
      const structuredCategories = buildCategoryTree(data);
      setSidebarCategories(structuredCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleGoogleLogin = (user: GoogleUser) => {
    setGoogleUser(user);
    localStorage.setItem("user", JSON.stringify(user));
    setUserDropdownOpen(true);
  };

  const handleLogout = () => {
    setGoogleUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.dispatchEvent(new Event("storage"));
    setIsSidebarOpen(false);
    setUserDropdownOpen(false);
    navigate('/');
  };

  const fetchSuggestions = async (value: string) => {
    if (value.length < 1) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await fetch(`https://www.ayushavtika.com/api/search/suggestions?q=${value}`);
      // const res = await fetch(`https://www.ayushvatika.com/api/search/suggestions?q=${value}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setSuggestions(data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    }
  };

  const handleSearch = () => {
    if (query.trim()) {
      setSuggestions([]);
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsSearchOverlayOpen(false);
    }
  };


  const headerNavLinks = [
     { name: "What's Ayurveda", path: "/explore-ayushvatika" },
    // { name: "SKIN", path: "/skin" },
    // { name: "Products", path: "/ayushvatika-product" },
    
    { name: "About", path: "/about" },
    { name: "Ayurveda Blogs", path: "/blog" },
    { name: "Appointments", path: "/book-appointments" },
  ];

  return (
    <>
      {isAuthModalOpen && (
        <AuthModal
  onClose={() => setIsAuthModalOpen(false)}
  onLoginSuccess={handleGoogleLogin} // <--- Yahan change karein
/>
)}

      {isSearchOverlayOpen && (
        <div className="fixed inset-0 bg-white z-40 flex flex-col p-4 animate-fade-in-down">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Search Products</h2>
            <button onClick={() => setIsSearchOverlayOpen(false)} className="text-gray-600 text-3xl">
              &times;
            </button>
          </div>
          <div className="relative flex-grow">
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                fetchSuggestions(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Search Products..."
              className="w-full px-4 py-3 border rounded-lg pr-10 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
            <FaSearch
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer text-lg"
              onClick={handleSearch}
            />
            {suggestions.length > 0 && (
              <div className="absolute bg-white border w-full mt-1 rounded shadow-lg z-10 top-full max-h-60 overflow-y-auto">
                {suggestions.map((s, index) => (
                  <div key={index} className="p-3 hover:bg-gray-100 cursor-pointer text-base" onClick={() => {
                    setQuery(s);
                    setSuggestions([]);
                    navigate(`/search?q=${encodeURIComponent(s)}`);
                    setIsSearchOverlayOpen(false);
                  }}>{s}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        sidebarItems={sidebarCategories}
        googleUser={googleUser}
        onLogout={handleLogout}
        openLoginModal={() => setIsAuthModalOpen(true)}
        ref={sidebarRef}
        headerHeight={headerRef.current?.offsetHeight || 0}
      />

      {/* Attach ref to the header element */}
      <header className={'sticky top-0 z-30 bg-white'} ref={headerRef}>
        
       {/* Top Marquee Bar */}
<div className="bg-[#4a6b2e] text-green-100 text-sm py-1 overflow-hidden relative">
  <div className="whitespace-nowrap animate-marquee-left">
  🌿 100 % Herbal | 💚 Ayurvedic Skin Care | 🌸 Flat 20% Off on Herbal Oils | 🍀 Pure Ayurveda, Pure You | 🌿 Free Shipping on Herbal Combos
  </div>
</div>


        {/* Main Header Bar */}
        <div className="flex justify-between items-center px-4 py-3 shadow-md h-24">
          {/* Logo and Mobile Menu */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div
              className="text-xl sm:text-2xl text-gray-700 cursor-pointer"
              onMouseEnter={() => setIsSidebarOpen(true)}
              onClick={() => setIsSidebarOpen(true)}
            >
              <FaBars />
            </div>
           <Link 
  to="/" 
  className="text-xl sm:text-2xl font-bold text-green-700 " 
 style={{ fontFamily: "'Pacifico'" }}

>
<div className="flex items-center gap-1">
  <img
    src={logo}
    alt="Ayush Vatika Logo"
    className="h-32 md:h-52 object-contain"
  />
  {/* <h2 className="text-2xl font-semibold text-green-700 leading-none">
    Zindagi ki Surwat
  </h2> */}
</div>

</Link>

          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-gray-700 font-medium uppercase">
            {headerNavLinks.map((link) => (
              <Link key={link.name} to={link.path} className={`hover:text-green-600 ${link.name === 'PURE SENSE' ? 'text-green-700 font-bold' : ''}`}>
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Icons and Login */}
          <div className="flex items-center space-x-3 sm:space-x-4 text-gray-600">
            {/* Desktop Search */}
            <div className="relative flex-grow max-w-xs md:max-w-md lg:max-w-lg hidden md:flex items-center">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  fetchSuggestions(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder="Search"
                className="w-40 px-3 py-1 text-sm border-b border-gray-300 focus:outline-none focus:border-green-500"
              />
              <FaSearch
                className="absolute right-0 text-gray-500 cursor-pointer"
                onClick={handleSearch}
              />
              {suggestions.length > 0 && (
                <div className="absolute bg-white border w-full mt-1 rounded shadow z-10 top-full">
                  {suggestions.map((s, index) => (
                    <div key={index} className="p-2 hover:bg-gray-100 cursor-pointer text-sm" onClick={() => {
                      setQuery(s);
                      setSuggestions([]);
                      navigate(`/search?q=${encodeURIComponent(s)}`);
                    }}>{s}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Search Icon */}
            <div className="md:hidden text-lg cursor-pointer" onClick={() => setIsSearchOverlayOpen(true)}>
              <FaSearch />
            </div>

           <Link to="/cart" className="relative">
      <FaShoppingBag className="text-lg cursor-pointer hover:text-green-600" />
      {itemCount > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full px-1">
          {itemCount}
        </span>
      )}
    </Link>

            {googleUser ? (
              <div className="relative" ref={userDropdownRef}>
                <button className="flex items-center focus:outline-none" onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                  <img
                    src={googleUser.picture || "/default-avatar.png"}
                    alt="Profile"
                    className="w-7 h-7 rounded-full border-2 border-green-600 object-cover"
                    // Add onError to handle broken image links by falling back to default
                    onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }}
                  />
                  <FaChevronDown className={`text-xs ml-1 transition-transform ${userDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                </button>
                {userDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white border rounded-lg shadow-lg z-20 text-sm">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      Signed in as<br />
                      <span className="font-semibold truncate">{googleUser.name}</span>
                    </div>
                    {/* <Link to="/profile" className="block px-4 py-2 text-gray-800 hover:bg-gray-100" onClick={() => setUserDropdownOpen(false)}>
                      Profile
                    </Link> */}
                    <Link to="/settings" className="block px-4 py-2 text-gray-800 hover:bg-gray-100" onClick={() => setUserDropdownOpen(false)}>
                      Settings
                    </Link>
                    <button onClick={handleLogout} className="w-full text-left block px-4 py-2 text-gray-800 hover:bg-gray-100 border-t">
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="hidden md:flex items-center gap-1 bg-white text-green-700 border border-green-600 px-3 py-1 rounded-full shadow-sm hover:bg-green-100 transition font-semibold text-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4S8 5.79 8 8s1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4
                    v2h16v-2c0-2.66-5.33-4-8-4z"
                  />
                </svg>
                Login
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;